import React, { useEffect, useState } from 'react';
import { UserCircleIcon, CalendarIcon, CheckCircleIcon, TrendingUpIcon, DollarSignIcon, PhoneIcon, MailIcon, MapPinIcon, ShieldIcon, FileTextIcon, MessageSquareIcon, PackageIcon } from 'lucide-react';
import { Card } from '../ui/Card';
import { Select } from '../ui/Select';
import { Badge } from '../ui/Badge';
import { Tabs } from '../ui/Tabs';
import { useData } from '../../contexts/DataContext';
import { useAuth } from '../../contexts/AuthContext';
import { Patient, Appointment, ClinicalHistory, FollowUpNote, Opportunity } from '../../types';
import { formatDate, formatTime, formatCurrency, getStatusName, getStageName, getStageColor } from '../../utils/helpers';
import { supabase } from '../../utils/supabase';
export function Patient360() {
  const {
    patients,
    appointments,
    clinicalHistories,
    followUpNotes,
    opportunities,
    getInsurance,
    getDoctor,
    getService,
    getSpecialty
  } = useData();
  const [selectedPatientId, setSelectedPatientId] = useState('');
  const [productSales, setProductSales] = useState<any[]>([]);
  const [isLoadingSales, setIsLoadingSales] = useState(false);
  // Filter active patients
  const [patientStatuses, setPatientStatuses] = useState<Record<string, 'archived' | 'deactivated'>>({});
  useEffect(() => {
    try {
      const saved = localStorage.getItem('savidna-patients-status');
      if (saved) setPatientStatuses(JSON.parse(saved));
    } catch {}
  }, []);
  const activePatients = patients.filter(p => !patientStatuses[p.id]);
  const patientOptions = [{
    value: '',
    label: 'Seleccionar paciente...'
  }, ...activePatients.map(p => ({
    value: p.id,
    label: p.name
  }))];
  const selectedPatient = patients.find(p => p.id === selectedPatientId);
  // Fetch product sales when patient changes
  useEffect(() => {
    if (selectedPatientId) {
      const fetchSales = async () => {
        setIsLoadingSales(true);
        try {
          const {
            data,
            error
          } = await supabase.from('product_sales').select('*').eq('patient_id', selectedPatientId).order('date', {
            ascending: false
          });
          if (!error && data) {
            setProductSales(data);
          }
        } catch (err) {
          console.error('Error fetching product sales:', err);
        } finally {
          setIsLoadingSales(false);
        }
      };
      fetchSales();
    } else {
      setProductSales([]);
    }
  }, [selectedPatientId]);
  // Derived data
  const patientAppointments = appointments.filter(a => a.patientId === selectedPatientId);
  const patientHistories = clinicalHistories.filter(h => h.patientId === selectedPatientId);
  // For follow up notes, we match by contactName (since notes use contactName instead of patientId)
  // This is a best-effort match based on the current data structure
  const patientNotes = followUpNotes.filter(n => n.contactName === selectedPatient?.name);
  // Opportunities use contactId, which might not map 1:1 to patientId, but we'll try to match by name or if contactId matches
  const patientOpportunities = opportunities.filter(o => {
    // If we have a direct link (assuming contactId might sometimes hold patientId in this CRM setup)
    if (o.contactId === selectedPatientId) return true;
    // Otherwise, try to match by name (since contact and patient might be separate entities with same name)
    if (selectedPatient && o.name.toLowerCase().includes(selectedPatient.name.toLowerCase())) return true;
    return false;
  });
  // KPIs
  const totalAppointments = patientAppointments.length;
  const completedAppointments = patientAppointments.filter(a => a.status === 'completada').length;
  const today = new Date().toISOString().split('T')[0];
  const upcomingAppointments = patientAppointments.filter(a => a.status === 'programada' && a.date >= today).length;
  // Calculate total spent (completed appointments + product sales)
  const totalSpentAppointments = patientAppointments.filter(a => a.status === 'completada').reduce((sum, a) => {
    // Handle multiple services
    if (a.serviceIds && a.serviceIds.length > 0) {
      return sum + a.serviceIds.reduce((sSum, sId) => {
        const svc = getService(sId);
        return sSum + (svc?.price || 0);
      }, 0);
    }
    // Single service fallback
    const svc = getService(a.serviceId);
    return sum + (svc?.price || 0);
  }, 0);
  const totalSpentProducts = productSales.reduce((sum, sale) => sum + Number(sale.total || 0), 0);
  const totalSpent = totalSpentAppointments + totalSpentProducts;
  const getInitials = (name: string) => {
    return name.split(' ').map(n => n[0]).join('').substring(0, 2).toUpperCase();
  };
  return <div className="space-y-6" data-id="element-2988">
      {/* Header Banner */}
      <div className="relative overflow-hidden rounded-2xl bg-gradient-to-br from-teal-600 via-teal-500 to-cyan-500 p-6 text-white shadow-xl" data-id="element-2989">
        <div className="absolute top-0 right-0 w-64 h-64 bg-white/10 rounded-full blur-3xl -translate-y-1/2 translate-x-1/2" data-id="element-2990" />
        <div className="relative z-10 flex flex-col md:flex-row md:items-center md:justify-between gap-4" data-id="element-2991">
          <div data-id="element-2992">
            <div className="flex items-center gap-3 mb-2" data-id="element-2993">
              <div className="p-2.5 bg-white/15 backdrop-blur-sm rounded-xl" data-id="element-2994">
                <UserCircleIcon className="w-6 h-6" data-id="element-2995" />
              </div>
              <h1 className="text-2xl font-bold" data-id="element-2996">Cliente 360</h1>
            </div>
            <p className="text-teal-100 max-w-xl" data-id="element-2997">
              Vista unificada del paciente
            </p>
          </div>
        </div>
      </div>

      {/* Selector */}
      <Card data-id="element-2998">
        <div className="max-w-md" data-id="element-2999">
          <Select label="Seleccionar Paciente" options={patientOptions} value={selectedPatientId} onChange={setSelectedPatientId} searchable placeholder="Buscar paciente..." data-id="element-3000" />
        </div>
      </Card>

      {!selectedPatient ? <div className="flex flex-col items-center justify-center py-20 text-center" data-id="element-3001">
          <div className="w-20 h-20 bg-gray-100 dark:bg-gray-800 rounded-full flex items-center justify-center mb-6" data-id="element-3002">
            <UserCircleIcon className="w-10 h-10 text-gray-400" data-id="element-3003" />
          </div>
          <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-2" data-id="element-3004">
            Selecciona un paciente
          </h2>
          <p className="text-gray-500 dark:text-gray-400 max-w-md" data-id="element-3005">
            Busca y selecciona un paciente en el menú desplegable para ver toda
            su información unificada en esta pantalla.
          </p>
        </div> : <div className="space-y-6" data-id="element-3006">
          {/* Patient Profile Card */}
          <Card className="border-t-4 border-t-teal-500 shadow-md overflow-hidden" data-id="element-3007">
            <div className="flex flex-col md:flex-row gap-6 items-start" data-id="element-3008">
              <div className="flex-shrink-0" data-id="element-3009">
                <div className="w-24 h-24 rounded-full bg-gradient-to-br from-teal-500 to-cyan-500 flex items-center justify-center text-white text-3xl font-bold shadow-lg" data-id="element-3010">
                  {getInitials(selectedPatient.name)}
                </div>
              </div>

              <div className="flex-1 min-w-0" data-id="element-3011">
                <div className="flex flex-col md:flex-row md:justify-between md:items-start gap-4" data-id="element-3012">
                  <div data-id="element-3013">
                    <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-1" data-id="element-3014">
                      {selectedPatient.name}
                    </h2>
                    <div className="flex flex-wrap items-center gap-3 text-sm text-gray-500 dark:text-gray-400 mb-4" data-id="element-3015">
                      <span className="capitalize" data-id="element-3016">
                        {selectedPatient.gender}
                      </span>
                      <span data-id="element-3017">•</span>
                      <span data-id="element-3018">{selectedPatient.age} años</span>
                      {selectedPatient.birthDate && <>
                          <span data-id="element-3019">•</span>
                          <span data-id="element-3020">Nacimiento: {selectedPatient.birthDate}</span>
                        </>}
                      {selectedPatient.cedula && <>
                          <span data-id="element-3021">•</span>
                          <span data-id="element-3022">Cédula: {selectedPatient.cedula}</span>
                        </>}
                    </div>
                  </div>

                  {selectedPatient.insuranceId && <Badge variant="info" className="flex items-center gap-1" data-id="element-3023">
                      <ShieldIcon className="w-3 h-3" data-id="element-3024" />
                      {getInsurance(selectedPatient.insuranceId)?.name || 'Seguro'}
                    </Badge>}
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-4" data-id="element-3025">
                  <div className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-300" data-id="element-3026">
                    <PhoneIcon className="w-4 h-4 text-gray-400" data-id="element-3027" />
                    <span data-id="element-3028">{selectedPatient.phone || 'Sin teléfono'}</span>
                  </div>
                  <div className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-300" data-id="element-3029">
                    <MailIcon className="w-4 h-4 text-gray-400" data-id="element-3030" />
                    <span className="truncate" data-id="element-3031">
                      {selectedPatient.email || 'Sin email'}
                    </span>
                  </div>
                  <div className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-300" data-id="element-3032">
                    <MapPinIcon className="w-4 h-4 text-gray-400" data-id="element-3033" />
                    <span className="truncate" data-id="element-3034">
                      {selectedPatient.address || 'Sin dirección'}
                    </span>
                  </div>
                </div>

                {selectedPatient.emergencyContact?.name && <div className="bg-gray-50 dark:bg-gray-800/50 rounded-lg p-3 border border-gray-100 dark:border-gray-700 inline-block" data-id="element-3035">
                    <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1" data-id="element-3036">
                      Contacto de Emergencia
                    </p>
                    <p className="text-sm font-medium text-gray-900 dark:text-white" data-id="element-3037">
                      {selectedPatient.emergencyContact.name} (
                      {selectedPatient.emergencyContact.relationship})
                    </p>
                    <p className="text-sm text-gray-600 dark:text-gray-400" data-id="element-3038">
                      {selectedPatient.emergencyContact.phone}
                    </p>
                  </div>}
              </div>
            </div>
          </Card>

          {/* KPI Cards */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4" data-id="element-3039">
            <Card padding="sm" className="bg-sky-50 dark:bg-sky-900/20 border-sky-100 dark:border-sky-800/50" data-id="element-3040">
              <div className="flex items-center gap-3" data-id="element-3041">
                <div className="p-3 bg-sky-100 dark:bg-sky-800/50 rounded-lg text-sky-600 dark:text-sky-400" data-id="element-3042">
                  <CalendarIcon className="w-6 h-6" data-id="element-3043" />
                </div>
                <div data-id="element-3044">
                  <p className="text-sm font-medium text-sky-600 dark:text-sky-400" data-id="element-3045">
                    Total Citas
                  </p>
                  <p className="text-2xl font-bold text-gray-900 dark:text-white" data-id="element-3046">
                    {totalAppointments}
                  </p>
                </div>
              </div>
            </Card>

            <Card padding="sm" className="bg-emerald-50 dark:bg-emerald-900/20 border-emerald-100 dark:border-emerald-800/50" data-id="element-3047">
              <div className="flex items-center gap-3" data-id="element-3048">
                <div className="p-3 bg-emerald-100 dark:bg-emerald-800/50 rounded-lg text-emerald-600 dark:text-emerald-400" data-id="element-3049">
                  <CheckCircleIcon className="w-6 h-6" data-id="element-3050" />
                </div>
                <div data-id="element-3051">
                  <p className="text-sm font-medium text-emerald-600 dark:text-emerald-400" data-id="element-3052">
                    Completadas
                  </p>
                  <p className="text-2xl font-bold text-gray-900 dark:text-white" data-id="element-3053">
                    {completedAppointments}
                  </p>
                </div>
              </div>
            </Card>

            <Card padding="sm" className="bg-amber-50 dark:bg-amber-900/20 border-amber-100 dark:border-amber-800/50" data-id="element-3054">
              <div className="flex items-center gap-3" data-id="element-3055">
                <div className="p-3 bg-amber-100 dark:bg-amber-800/50 rounded-lg text-amber-600 dark:text-amber-400" data-id="element-3056">
                  <TrendingUpIcon className="w-6 h-6" data-id="element-3057" />
                </div>
                <div data-id="element-3058">
                  <p className="text-sm font-medium text-amber-600 dark:text-amber-400" data-id="element-3059">
                    Próximas
                  </p>
                  <p className="text-2xl font-bold text-gray-900 dark:text-white" data-id="element-3060">
                    {upcomingAppointments}
                  </p>
                </div>
              </div>
            </Card>

            <Card padding="sm" className="bg-purple-50 dark:bg-purple-900/20 border-purple-100 dark:border-purple-800/50" data-id="element-3061">
              <div className="flex items-center gap-3" data-id="element-3062">
                <div className="p-3 bg-purple-100 dark:bg-purple-800/50 rounded-lg text-purple-600 dark:text-purple-400" data-id="element-3063">
                  <DollarSignIcon className="w-6 h-6" data-id="element-3064" />
                </div>
                <div data-id="element-3065">
                  <p className="text-sm font-medium text-purple-600 dark:text-purple-400" data-id="element-3066">
                    Total Gastado
                  </p>
                  <p className="text-xl font-bold text-gray-900 dark:text-white" data-id="element-3067">
                    {formatCurrency(totalSpent)}
                  </p>
                </div>
              </div>
            </Card>
          </div>

          {/* Tabs */}
          <Tabs tabs={[{
        id: 'citas',
        label: `Citas (${patientAppointments.length})`,
        icon: <CalendarIcon className="w-4 h-4" data-id="element-3069" />,
        content: <Card padding="none" data-id="element-3070">
                    <div className="overflow-x-auto" data-id="element-3071">
                      <table className="w-full" data-id="element-3072">
                        <thead className="bg-gray-50 dark:bg-gray-800/50 border-b border-gray-200 dark:border-gray-700" data-id="element-3073">
                          <tr data-id="element-3074">
                            <th className="text-left py-3 px-4 text-xs font-semibold text-gray-500 uppercase" data-id="element-3075">
                              Fecha
                            </th>
                            <th className="text-left py-3 px-4 text-xs font-semibold text-gray-500 uppercase" data-id="element-3076">
                              Doctor
                            </th>
                            <th className="text-left py-3 px-4 text-xs font-semibold text-gray-500 uppercase" data-id="element-3077">
                              Servicio
                            </th>
                            <th className="text-left py-3 px-4 text-xs font-semibold text-gray-500 uppercase" data-id="element-3078">
                              Estado
                            </th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-200 dark:divide-gray-700" data-id="element-3079">
                          {patientAppointments.length === 0 ? <tr data-id="element-3080">
                              <td colSpan={4} className="py-8 text-center text-gray-500" data-id="element-3081">
                                No hay citas registradas
                              </td>
                            </tr> : patientAppointments.sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime()).map(apt => {
                  const doctor = getDoctor(apt.doctorId);
                  const serviceIds = apt.serviceIds && apt.serviceIds.length > 0 ? apt.serviceIds : [apt.serviceId];
                  const serviceNames = serviceIds.map(id => getService(id)?.name).filter(Boolean).join(', ');
                  return <tr key={apt.id} className="hover:bg-gray-50 dark:hover:bg-gray-800/50" data-id="element-3082">
                                    <td className="py-3 px-4 text-sm" data-id="element-3083">
                                      <div className="font-medium text-gray-900 dark:text-white" data-id="element-3084">
                                        {formatDate(apt.date)}
                                      </div>
                                      <div className="text-gray-500 text-xs" data-id="element-3085">
                                        {formatTime(apt.time)}
                                      </div>
                                    </td>
                                    <td className="py-3 px-4 text-sm text-gray-900 dark:text-gray-300" data-id="element-3086">
                                      {doctor ? `Dr. ${doctor.name}` : '-'}
                                    </td>
                                    <td className="py-3 px-4 text-sm text-gray-900 dark:text-gray-300" data-id="element-3087">
                                      {serviceNames || '-'}
                                    </td>
                                    <td className="py-3 px-4" data-id="element-3088">
                                      <Badge variant={apt.status === 'completada' ? 'success' : apt.status === 'cancelada' ? 'danger' : apt.status === 'reprogramada' ? 'warning' : 'info'} data-id="element-3089">
                                        {getStatusName(apt.status)}
                                      </Badge>
                                    </td>
                                  </tr>;
                })}
                        </tbody>
                      </table>
                    </div>
                  </Card>
      }, {
        id: 'historial',
        label: `Historial Clínico (${patientHistories.length})`,
        icon: <FileTextIcon className="w-4 h-4" data-id="element-3090" />,
        content: <Card padding="none" data-id="element-3091">
                    <div className="overflow-x-auto" data-id="element-3092">
                      <table className="w-full" data-id="element-3093">
                        <thead className="bg-gray-50 dark:bg-gray-800/50 border-b border-gray-200 dark:border-gray-700" data-id="element-3094">
                          <tr data-id="element-3095">
                            <th className="text-left py-3 px-4 text-xs font-semibold text-gray-500 uppercase" data-id="element-3096">
                              Fecha
                            </th>
                            <th className="text-left py-3 px-4 text-xs font-semibold text-gray-500 uppercase" data-id="element-3097">
                              Doctor
                            </th>
                            <th className="text-left py-3 px-4 text-xs font-semibold text-gray-500 uppercase" data-id="element-3098">
                              Motivo
                            </th>
                            <th className="text-left py-3 px-4 text-xs font-semibold text-gray-500 uppercase" data-id="element-3099">
                              Diagnóstico
                            </th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-200 dark:divide-gray-700" data-id="element-3100">
                          {patientHistories.length === 0 ? <tr data-id="element-3101">
                              <td colSpan={4} className="py-8 text-center text-gray-500" data-id="element-3102">
                                No hay historiales registrados
                              </td>
                            </tr> : patientHistories.sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime()).map(h => <tr key={h.id} className="hover:bg-gray-50 dark:hover:bg-gray-800/50" data-id="element-3103">
                                  <td className="py-3 px-4 text-sm font-medium text-gray-900 dark:text-white" data-id="element-3104">
                                    {formatDate(h.date.toISOString().split('T')[0])}
                                  </td>
                                  <td className="py-3 px-4 text-sm text-gray-900 dark:text-gray-300" data-id="element-3105">
                                    {h.doctorName || '-'}
                                  </td>
                                  <td className="py-3 px-4 text-sm text-gray-600 dark:text-gray-400 truncate max-w-[200px]" data-id="element-3106">
                                    {h.personalBackground?.consultationReason || '-'}
                                  </td>
                                  <td className="py-3 px-4 text-sm text-gray-600 dark:text-gray-400 truncate max-w-[200px]" data-id="element-3107">
                                    {h.diagnosis || '-'}
                                  </td>
                                </tr>)}
                        </tbody>
                      </table>
                    </div>
                  </Card>
      }, {
        id: 'notas',
        label: `Notas (${patientNotes.length})`,
        icon: <MessageSquareIcon className="w-4 h-4" data-id="element-3108" />,
        content: <div className="space-y-4" data-id="element-3109">
                    {patientNotes.length === 0 ? <Card data-id="element-3110">
                        <div className="py-8 text-center text-gray-500" data-id="element-3111">
                          No hay notas de seguimiento
                        </div>
                      </Card> : patientNotes.sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()).map(note => <Card key={note.id} className="bg-gray-50 dark:bg-gray-800/30" data-id="element-3112">
                            <div className="flex justify-between items-start mb-2" data-id="element-3113">
                              <h3 className="font-semibold text-gray-900 dark:text-white" data-id="element-3114">
                                {note.subject}
                              </h3>
                              <span className="text-xs text-gray-500" data-id="element-3115">
                                {new Date(note.createdAt).toLocaleDateString()}
                              </span>
                            </div>
                            <p className="text-sm text-gray-700 dark:text-gray-300 whitespace-pre-wrap" data-id="element-3116">
                              {note.description}
                            </p>
                            <div className="mt-3 text-xs text-gray-500" data-id="element-3117">
                              Por: {note.userName}
                            </div>
                          </Card>)}
                  </div>
      }, {
        id: 'oportunidades',
        label: `Oportunidades (${patientOpportunities.length})`,
        icon: <TrendingUpIcon className="w-4 h-4" data-id="element-3118" />,
        content: <Card padding="none" data-id="element-3119">
                    <div className="overflow-x-auto" data-id="element-3120">
                      <table className="w-full" data-id="element-3121">
                        <thead className="bg-gray-50 dark:bg-gray-800/50 border-b border-gray-200 dark:border-gray-700" data-id="element-3122">
                          <tr data-id="element-3123">
                            <th className="text-left py-3 px-4 text-xs font-semibold text-gray-500 uppercase" data-id="element-3124">
                              Nombre
                            </th>
                            <th className="text-left py-3 px-4 text-xs font-semibold text-gray-500 uppercase" data-id="element-3125">
                              Tipo
                            </th>
                            <th className="text-left py-3 px-4 text-xs font-semibold text-gray-500 uppercase" data-id="element-3126">
                              Etapa
                            </th>
                            <th className="text-right py-3 px-4 text-xs font-semibold text-gray-500 uppercase" data-id="element-3127">
                              Monto
                            </th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-200 dark:divide-gray-700" data-id="element-3128">
                          {patientOpportunities.length === 0 ? <tr data-id="element-3129">
                              <td colSpan={4} className="py-8 text-center text-gray-500" data-id="element-3130">
                                No hay oportunidades registradas
                              </td>
                            </tr> : patientOpportunities.sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()).map(opp => <tr key={opp.id} className="hover:bg-gray-50 dark:hover:bg-gray-800/50" data-id="element-3131">
                                  <td className="py-3 px-4 text-sm font-medium text-gray-900 dark:text-white" data-id="element-3132">
                                    {opp.name}
                                  </td>
                                  <td className="py-3 px-4 text-sm text-gray-600 dark:text-gray-400 capitalize" data-id="element-3133">
                                    {opp.opportunityType.replace(/_/g, ' ')}
                                  </td>
                                  <td className="py-3 px-4" data-id="element-3134">
                                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStageColor(opp.stage)}`} data-id="element-3135">
                                      {getStageName(opp.stage)}
                                    </span>
                                  </td>
                                  <td className="py-3 px-4 text-sm text-right font-medium text-gray-900 dark:text-white" data-id="element-3136">
                                    {formatCurrency(opp.amount)}
                                  </td>
                                </tr>)}
                        </tbody>
                      </table>
                    </div>
                  </Card>
      }, {
        id: 'productos',
        label: `Productos (${productSales.length})`,
        icon: <PackageIcon className="w-4 h-4" data-id="element-3137" />,
        content: <Card padding="none" data-id="element-3138">
                    <div className="overflow-x-auto" data-id="element-3139">
                      <table className="w-full" data-id="element-3140">
                        <thead className="bg-gray-50 dark:bg-gray-800/50 border-b border-gray-200 dark:border-gray-700" data-id="element-3141">
                          <tr data-id="element-3142">
                            <th className="text-left py-3 px-4 text-xs font-semibold text-gray-500 uppercase" data-id="element-3143">
                              Fecha
                            </th>
                            <th className="text-left py-3 px-4 text-xs font-semibold text-gray-500 uppercase" data-id="element-3144">
                              Productos
                            </th>
                            <th className="text-right py-3 px-4 text-xs font-semibold text-gray-500 uppercase" data-id="element-3145">
                              Total
                            </th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-200 dark:divide-gray-700" data-id="element-3146">
                          {isLoadingSales ? <tr data-id="element-3147">
                              <td colSpan={3} className="py-8 text-center text-gray-500" data-id="element-3148">
                                Cargando ventas...
                              </td>
                            </tr> : productSales.length === 0 ? <tr data-id="element-3149">
                              <td colSpan={3} className="py-8 text-center text-gray-500" data-id="element-3150">
                                No hay compras de productos
                              </td>
                            </tr> : productSales.map(sale => {
                  const items = Array.isArray(sale.items) ? sale.items : [];
                  const itemsDesc = items.map((i: any) => `${i.productName || 'Producto'} (x${i.quantity})`).join(', ');
                  return <tr key={sale.id} className="hover:bg-gray-50 dark:hover:bg-gray-800/50" data-id="element-3151">
                                  <td className="py-3 px-4 text-sm font-medium text-gray-900 dark:text-white" data-id="element-3152">
                                    {formatDate(sale.date)}
                                  </td>
                                  <td className="py-3 px-4 text-sm text-gray-600 dark:text-gray-400" data-id="element-3153">
                                    {itemsDesc || 'Venta de productos'}
                                  </td>
                                  <td className="py-3 px-4 text-sm text-right font-medium text-emerald-600 dark:text-emerald-400" data-id="element-3154">
                                    {formatCurrency(sale.total)}
                                  </td>
                                </tr>;
                })}
                        </tbody>
                      </table>
                    </div>
                  </Card>
      }]} data-id="element-3068" />
        </div>}
    </div>;
}